import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { clampRange, normalizedSample, peakColumns, samplePoints, timeTicks, zoomRange } from './waveformMath';
import { useCanvasSize } from './useCanvasSize';

export function Waveform({ samples, sampleRate, position, loop }: { samples: number[]; sampleRate: number; position: number; loop?: { startSample: number; endSample: number } | null }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const viewport = useRef<HTMLDivElement>(null);
  const [range, setRange] = useState({ start: 0, end: samples.length });
  const [follow, setFollow] = useState(false);
  const size = useCanvasSize(viewport, 340);
  const span = range.end - range.start;
  const zoom = samples.length / span;
  const spp = span / size.cssWidth;
  const duration = samples.length / sampleRate;
  const percent = (sample: number) => (sample - range.start) / span * 100;
  useEffect(() => { setRange({ start: 0, end: samples.length }); }, [samples]);
  // The scroll track is virtual; only a viewport-sized sticky canvas is rasterized.
  useLayoutEffect(() => {
    const node = viewport.current; if (node) node.scrollLeft = range.start / span * size.cssWidth;
  }, [range, span, size.cssWidth]);
  useEffect(() => {
    const node = canvas.current; if (!node) return;
    const context = node.getContext('2d'); if (!context) return;
    context.clearRect(0, 0, size.width, size.height);
    const y = (level: number) => (18 + (1 - level) * (340 - 26 - 18) / 2) * size.dpr;
    context.fillStyle = '#58d6b7';
    if (spp > 1) {
      const columns = peakColumns(samples, range, Math.ceil(size.cssWidth));
      columns.forEach((column, x) => {
        const left = Math.floor(x / columns.length * size.width);
        const right = Math.ceil((x + 1) / columns.length * size.width);
        const top = Math.floor(y(normalizedSample(column.max)));
        const bottom = Math.ceil(y(normalizedSample(column.min)));
        context.fillRect(left, top, Math.max(1, right - left), Math.max(1, bottom - top));
      });
    } else {
      const points = samplePoints(samples, range, size.cssWidth);
      context.strokeStyle = '#58d6b7'; context.lineWidth = size.dpr; context.beginPath();
      points.forEach((point, i) => { const x = point.x * size.dpr; if (i) context.lineTo(x, y(point.level)); else context.moveTo(x, y(point.level)); });
      context.stroke();
      if (spp <= .25) points.forEach(point => { context.beginPath(); context.arc(point.x * size.dpr, y(point.level), 1.5 * size.dpr, 0, Math.PI * 2); context.fill(); });
    }
    node.dataset.drawRevision = String(Number(node.dataset.drawRevision || 0) + 1);
  }, [samples, range, size, spp]);
  // Cursor movement alone is an overlay update, not a PCM redraw.
  useEffect(() => {
    const sample = position * sampleRate;
    if (follow && zoom > 1 && (sample < range.start || sample > range.end)) {
      setRange(clampRange(sample - span / 2, span, samples.length));
    }
  }, [position, sampleRate, follow, zoom, range, span, samples.length]);
  const cursor = percent(position * sampleRate);
  const loopStart = loop ? percent(loop.startSample) : 0, loopEnd = loop ? percent(loop.endSample) : 0;
  return <div className="waveform-shell">
    <div className="waveform-toolbar"><label className="check"><input type="checkbox" checked={follow} onChange={event => setFollow(event.target.checked)} />Follow Voice playhead</label><div>
      <button disabled={span <= Math.min(32, samples.length)} onClick={() => setRange(value => zoomRange(value, 2, samples.length))}>Zoom In</button>
      <button disabled={zoom <= 1} onClick={() => setRange(value => zoomRange(value, .5, samples.length))}>Zoom Out</button>
      <button onClick={() => setRange({ start: 0, end: samples.length })}>Reset / Fit</button>
    </div></div>
    <div className="waveform-body"><div className="amplitude-axis"><span>+1.0</span><span>0.0</span><span>−1.0</span></div>
      <div ref={viewport} className="waveform-scroll" onScroll={event => {
        const start = event.currentTarget.scrollLeft / size.cssWidth * span;
        if (Math.abs(start - range.start) > .01) setRange(clampRange(start, span, samples.length));
      }} onPointerDown={event => { if (zoom > 1) event.currentTarget.setPointerCapture(event.pointerId); }}
      onPointerMove={event => { if (event.currentTarget.hasPointerCapture(event.pointerId) && event.buttons === 1) event.currentTarget.scrollLeft -= event.movementX; }}
      onPointerUp={event => { if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId); }}>
        <div className="waveform-track" style={{ width: `${zoom * 100}%` }}><div className="waveform-plot" style={{ width: size.cssWidth, height: 340 }}>
          {loop && loopEnd > 0 && loopStart < 100 && <div className="loop-shading" style={{ left: `${Math.max(0, loopStart)}%`, width: `${Math.min(100, loopEnd) - Math.max(0, loopStart)}%` }} />}
          <div className="waveform-zero" />
          <canvas ref={canvas} width={size.width} height={size.height} className="waveform" role="img" aria-label="Voice waveform" aria-describedby="voice-waveform-summary"
            data-playhead={position.toFixed(4)} data-range-start={range.start} data-range-end={range.end} data-render-mode={spp > 1 ? 'min/max envelope' : 'sample-level detail'} />
          {cursor >= 0 && cursor <= 100 && <div className="voice-playhead" aria-hidden="true" style={{ left: `${cursor}%` }} />}
          {loop && [loopStart, loopEnd].map((x, index) => x >= 0 && x <= 100 && <span key={index} className={`loop-label ${index ? 'loop-end' : ''}`} style={{ left: `${x}%` }}>{index ? 'Loop end' : 'Loop start'}</span>)}
          <div className="waveform-time-axis">{timeTicks(range, sampleRate, size.cssWidth).map(tick => <span key={tick.label} style={{ left: `${tick.x}%`, transform: tick.x < 3 ? 'none' : tick.x > 97 ? 'translateX(-100%)' : undefined }}>{tick.label}</span>)}</div>
        </div></div>
      </div>
    </div>
    <p id="voice-waveform-summary" className="microcopy">{samples.length} samples · {duration.toFixed(3)} seconds · normalized amplitude / time axis{loop ? ' · marked loop start/end' : ''} · {spp > 1 ? 'min/max envelope' : 'sample-level detail'} · {(range.start / sampleRate).toFixed(4)}–{(range.end / sampleRate).toFixed(4)} s visible</p>
  </div>;
}
